import React, { useState, useCallback, useEffect } from 'react';
import { useMap } from '../map/MapContext';
import { formatCoordinateDisplay, parseCoordinateString } from '@shared/utils/coordinates';

export type CoordFormat = 'DD' | 'DMS' | 'DDM' | 'UTM' | 'MGRS' | 'GARS' | 'USNG';

function formatDD(lat: number, lng: number): string {
  return `${lat.toFixed(6)}°  ${lng.toFixed(6)}°`;
}

function toDMS(v: number, pos: string, neg: string): string {
  const dir = v >= 0 ? pos : neg;
  const abs = Math.abs(v);
  const d = Math.floor(abs);
  const m = Math.floor((abs - d) * 60);
  const s = ((abs - d - m / 60) * 3600).toFixed(2);
  return `${d}°${String(m).padStart(2, '0')}'${s}"${dir}`;
}

function formatDMS(lat: number, lng: number): string {
  return `${toDMS(lat, 'N', 'S')}  ${toDMS(lng, 'E', 'W')}`;
}

function formatDDM(lat: number, lng: number): string {
  const f = (v: number, p: string, n: string) => {
    const dir = v >= 0 ? p : n;
    const abs = Math.abs(v);
    const d = Math.floor(abs);
    return `${d}°${((abs - d) * 60).toFixed(4)}'${dir}`;
  };
  return `${f(lat, 'N', 'S')}  ${f(lng, 'E', 'W')}`;
}

function formatUTM(lat: number, lng: number): string {
  const zone = Math.floor((lng + 180) / 6) + 1;
  const letter = lat >= 0 ? 'N' : 'S';
  return `Zone ${zone}${letter}  ${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

export function formatCoord(lat: number, lng: number, fmt: CoordFormat): string {
  switch (fmt) {
    case 'DD': return formatDD(lat, lng);
    case 'DMS': return formatDMS(lat, lng);
    case 'DDM': return formatDDM(lat, lng);
    case 'UTM': return formatUTM(lat, lng);
    case 'MGRS': return formatCoordinateDisplay(lat, lng, 'mgrs');
    case 'GARS': return formatCoordinateDisplay(lat, lng, 'gars');
    case 'USNG': return formatCoordinateDisplay(lat, lng, 'usng');
  }
}

export function getPlaceholder(fmt: CoordFormat): string {
  switch (fmt) {
    case 'MGRS': return 'e.g. 33UXP0450045000';
    case 'GARS': return 'e.g. 361HN37';
    case 'USNG': return 'e.g. 18S UJ 23487 06483';
    default: return 'lat, lng (e.g. 39.8, -98.5)';
  }
}

export const COORD_FORMATS: CoordFormat[] = ['DD', 'DMS', 'DDM', 'UTM', 'MGRS', 'GARS', 'USNG'];

export const CoordinatePanel: React.FC = () => {
  const { map } = useMap();
  const [format, setFormat] = useState<CoordFormat>('DD');
  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [clicked, setClicked] = useState<{ lat: number; lng: number } | null>(null);
  const [goToInput, setGoToInput] = useState('');

  // Track map center
  useEffect(() => {
    if (!map) return;
    const onMove = () => {
      const c = map.getCenter();
      setCenter({ lat: c.lat, lng: c.lng });
    };
    map.on('move', onMove);
    onMove();
    return () => { map.off('move', onMove); };
  }, [map]);

  // Track map clicks
  useEffect(() => {
    if (!map) return;
    const onClick = (e: any) => setClicked({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [map]);

  const goTo = useCallback(() => {
    if (!map || !goToInput.trim()) return;

    // Try MGRS/GARS/USNG parsing for those formats
    if (format === 'MGRS' || format === 'GARS' || format === 'USNG') {
      const formatKey = format.toLowerCase() as 'mgrs' | 'gars' | 'usng';
      const result = parseCoordinateString(goToInput, formatKey);
      if (result.success) {
        map.flyTo({ center: [result.lng, result.lat], zoom: 10 });
        return;
      }
    }

    // Fallback: parse as lat, lng
    const parts = goToInput.split(/[,\s]+/).map(Number).filter((n) => !isNaN(n));
    if (parts.length >= 2) {
      map.flyTo({ center: [parts[1], parts[0]], zoom: 10 });
    }
  }, [map, goToInput, format]);

  const copy = useCallback((lat: number, lng: number) => {
    navigator.clipboard.writeText(formatCoord(lat, lng, format));
  }, [format]);

  const FORMATS = COORD_FORMATS;

  return (
    <div>
      {/* Format selector */}
      <div className="panel-section">
        <div className="panel-section-title">Format</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {FORMATS.map((f) => (
            <button key={f} className={`chip ${format === f ? 'active' : ''}`} onClick={() => setFormat(f)}>{f}</button>
          ))}
        </div>
      </div>

      {/* Map center */}
      <div className="panel-section">
        <div className="panel-section-title">Map Center</div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, padding: '4px 0', wordBreak: 'break-all' }}>
          {formatCoord(center.lat, center.lng, format)}
        </div>
        <button className="btn btn-secondary" onClick={() => copy(center.lat, center.lng)} style={{ fontSize: 11 }}>📋 Copy</button>
      </div>

      {/* Clicked point */}
      {clicked && (
        <div className="panel-section">
          <div className="panel-section-title">Clicked Point</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, padding: '4px 0', wordBreak: 'break-all' }}>
            {formatCoord(clicked.lat, clicked.lng, format)}
          </div>
          <button className="btn btn-secondary" onClick={() => copy(clicked.lat, clicked.lng)} style={{ fontSize: 11 }}>📋 Copy</button>
        </div>
      )}

      {/* Go-to */}
      <div className="panel-section">
        <div className="panel-section-title">Go To Coordinate</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            className="input"
            placeholder={getPlaceholder(format)}
            value={goToInput}
            onChange={(e) => setGoToInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && goTo()}
          />
          <button className="btn btn-primary" onClick={goTo}>Go</button>
        </div>
      </div>
    </div>
  );
};
